import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import './style.scss';
import 'leaflet/dist/leaflet.css';
import { useEffect, useState } from 'react';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import { MapContainer, Marker, TileLayer, useMapEvents } from 'react-leaflet';
import { INITIAL_ADMIN_TASK_LOCATION_COORDINATES } from '../../../../app.config.vars/stored';
import pinIcon from '../../../../assets/icons/pin-map.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import Button from '../../../../ui/Button';
import CurrentLocationIcon from '../../../../assets/icons/current_location.svg';
import Input from '../../../../ui/Input';
import Tooltip from '../../../../ui/Tooltip';
import InfoIcon from '../../../../assets/icons/info.svg';
import ButtonsGroup from '../../../../ui/ButtonsGroup';
// Fix for Leaflet default icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});
const defaultIcon = new L.Icon({
    iconUrl: pinIcon,
    iconSize: [27, 42],
    iconAnchor: [13, 42],
    popupAnchor: [0, -42],
    shadowUrl: markerShadow,
});
const RallyLocationFields = ({ latitude, longitude, onLocationChange, onValidate, errors, }) => {
    const { t } = useTranslation();
    const [forceFitBounds, setForceFitBounds] = useState(true); // Start with true to center on load
    const [marker, setMarker] = useState(latitude !== null && longitude !== null ? [latitude, longitude] : null);
    const recenterMap = () => {
        setForceFitBounds(true);
        setTimeout(() => setForceFitBounds(false), 500);
    };
    const updateCoordinates = (lat, lon) => {
        onLocationChange(lat, lon);
        if (lat !== null && lon !== null) {
            setMarker([lat, lon]);
            recenterMap();
        }
        else {
            setMarker(null);
        }
    };
    const LocationMarker = () => {
        useMapEvents({
            click(event) {
                const { lat, lng } = event.latlng;
                updateCoordinates(lat, lng);
            },
        });
        return marker ? _jsx(Marker, { position: marker, icon: defaultIcon }) : null;
    };
    useEffect(() => {
        if (latitude !== null && longitude !== null && !isNaN(latitude) && !isNaN(longitude)) {
            setMarker([latitude, longitude]);
        }
        else {
            setMarker(null);
        }
    }, [latitude, longitude]);
    const handleUseCurrentLocation = () => {
        if (!navigator.geolocation) {
            alert(t('error.geolocation_not_supported'));
            return;
        }
        navigator.geolocation.getCurrentPosition((position) => {
            const { latitude, longitude } = position.coords;
            updateCoordinates(latitude, longitude);
        }, (error) => {
            console.error(error);
            alert(t('error.unable_to_get_location'));
        });
    };
    const RecenterMap = ({ position }) => {
        const map = useMapEvents({});
        useEffect(() => {
            if (forceFitBounds && position) {
                map.setView(position, 15);
                // Reset the flag after centering
                setTimeout(() => setForceFitBounds(false), 100);
            }
        }, [forceFitBounds, position, map]);
        return null;
    };
    const handleLatitudeChange = (event) => {
        const value = event.target.value;
        if (value === '') {
            onLocationChange(null, longitude);
        }
        else {
            const lat = parseFloat(value);
            if (!isNaN(lat)) {
                onLocationChange(lat, longitude);
            }
        }
        onValidate('latitude', value);
    };
    const handleLongitudeChange = (event) => {
        const value = event.target.value;
        if (value === '') {
            onLocationChange(latitude, null);
        }
        else {
            const lon = parseFloat(value);
            if (!isNaN(lon)) {
                onLocationChange(latitude, lon);
            }
        }
        onValidate('longitude', value);
    };
    const handleClearLocation = () => {
        updateCoordinates(null, null);
    };
    const mapCenter = latitude !== null && longitude !== null
        ? [latitude, longitude]
        : [INITIAL_ADMIN_TASK_LOCATION_COORDINATES.lat, INITIAL_ADMIN_TASK_LOCATION_COORDINATES.lng];
    return (_jsxs("div", { className: "rally-location-fields", children: [_jsxs("div", { className: "label", children: [t('label.rally_location'), _jsx(Tooltip, { content: t('tooltip.rally_location'), position: "top", children: _jsx(InfoIcon, { style: {
                                width: '16px',
                                height: '16px',
                                marginLeft: '4px',
                                cursor: 'help',
                                display: 'inline-block',
                                verticalAlign: 'middle',
                                marginBottom: '2px',
                            } }) })] }), _jsxs("div", { className: "map-container", children: [_jsxs(MapContainer, { center: mapCenter, zoom: 15, style: { height: '100%', width: '100%' }, children: [_jsx(TileLayer, { url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: '\u00A9 <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' }), _jsx(LocationMarker, {}), _jsx(RecenterMap, { position: marker })] }), _jsx("button", { className: "map-control-button", onClick: recenterMap, children: t('button.recenter_map') })] }), _jsxs("div", { className: "coordinates-inputs", children: [_jsx(Input, { type: "number", id: "latitude", value: latitude !== null ? String(latitude) : '', name: "latitude", label: t('label.coordinates_lat'), labelPosition: "left", labelWidth: "155px", onChange: handleLatitudeChange, error: !!errors.latitude?.length, errorMessage: !!errors.latitude?.length ? t(errors.latitude[0].messageTranslationKey) : undefined, placeholder: "-90 to 90" }), _jsx(Input, { type: "number", id: "longitude", value: longitude !== null ? String(longitude) : '', name: "longitude", label: t('label.coordinates_lon'), labelPosition: "left", labelWidth: "155px", onChange: handleLongitudeChange, error: !!errors.longitude?.length, errorMessage: !!errors.longitude?.length ? t(errors.longitude[0].messageTranslationKey) : undefined, placeholder: "-180 to 180" })] }), _jsx("div", { className: "location-controls", children: _jsxs(ButtonsGroup, { justify: 'end', children: [_jsx(Button, { variant: "outlined", onClick: handleUseCurrentLocation, icon: _jsx(CurrentLocationIcon, {}), label: t('button.use_current_location') }), marker && (_jsx(Button, { variant: "outlined", onClick: handleClearLocation, label: t('button.clear_location') }))] }) })] }));
};
export default RallyLocationFields;
